// src/routes/AppRoutes.jsx
import React, { Suspense } from "react"; // <--- 1. Import Suspense
import { Routes, Route, Navigate } from "react-router-dom";


import Sidebar from "../components/Sidebar";
import Footer from "../components/Footer";

// Keep lightweight pages as static imports (Instant load)
import Home from "../pages/Home";
import About from "../pages/About";
import Settings from "../pages/Settings";
import Ranking from "../pages/Ranking";
import PrivacyPolicy from "../pages/PrivacyPolicy";
import TermsOfService from "../pages/TermsOfService";

// SEO alias pages (Lightweight)
import RandomVideoChat from "../pages/landing/RandomVideoChat";
import MeetStrangersOnline from "../pages/landing/MeetStrangersOnline";
import TalkToStrangers from "../pages/landing/TalkToStrangers";
import VideoChatWithStrangers from "../pages/landing/VideoChatWithStrangers";
import FreeOnlineVideoChat from "../pages/landing/FreeOnlineVideoChat";
import OmeTVAlternative from "../pages/landing/OmeTVAlternative";
import MonkeyAlternative from "../pages/landing/MonkeyAlternative";
import OmegleAlternative from "../pages/landing/OmegleAlternative";
import ChatrouletteAlternative from "../pages/landing/ChatrouletteAlternative";
import CooMeetAlternative from "../pages/landing/CooMeetAlternative";
import EmeraldChatAlternative from "../pages/landing/EmeraldChatAlternative";
import AzarAlternative from "../pages/landing/AzarAlternative";
import TriviaVideoChat from "../pages/landing/TriviaVideoChat";
import LaughChallenge from "../pages/landing/LaughChallenge";
import TicTacToeVideoChat from "../pages/landing/TicTacToeVideoChat";
import Connect4VideoChat from "../pages/landing/Connect4VideoChat";
import AdFreeChat from "../pages/landing/AdFreeChat";
import NoLoginChat from "../pages/landing/NoLoginChat";

// --- LAZY LOAD HEAVY PAGES ---
// This prevents 'simple-peer' & 'mediapipe' from loading on landing pages!
const ChatPage = React.lazy(() => import("../pages/ChatPage"));
const VeeClipsFeed = React.lazy(() => import("../pages/VeeClipsFeed"));
const AdminDashboard = React.lazy(() => import("../pages/AdminDashboard"));

/**
 * Loading Spinner for Lazy Components
 */
const PageLoader = () => (
  <div style={{
    height: '100vh', 
    width: '100%', 
    display: 'flex', 
    justifyContent: 'center', 
    alignItems: 'center',
    background: '#fff',
    color: '#1a73e8'
  }}>
    <div className="spinner-border" role="status">
      <span className="visually-hidden">Loading...</span>
    </div>
  </div>
);

function NoPartnerLayout({ menuActive, setMenuActive, children }) {
  return (
    <div className="app-wrapper no-partner">
      <button
        className="menu-toggle"
        onClick={() => setMenuActive(!menuActive)}
      >
        ☰
      </button>

      <Sidebar menuActive={menuActive} setMenuActive={setMenuActive} />
      
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', width: '100%', overflowY: 'auto' }}>
        <div style={{ flex: 1 }}>
          {children}
        </div>
        <Footer />
      </div>
    </div>
  );
}

export default function AppRoutes({
  menuActive,
  setMenuActive,
  user,
  isAuthLoading,
  userCountry,
  onLogout, 
  autoSearchNext, 
  setAutoSearchNext, 
  openSignIn,
  showInstallButton,
  onInstallClick, 
}) {
  // Helper to keep code clean
  const renderWithShell = (Element) => (
    <NoPartnerLayout menuActive={menuActive} setMenuActive={setMenuActive}>
      <Element />
    </NoPartnerLayout>
  );

  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        {/* Simple pages without the shell wrapper */}
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/terms" element={<TermsOfService />} />

        {/* --- HEAVY ROUTES (Lazy Loaded) --- */}
        <Route
          path="/chat"
          element={
            <ChatPage
              user={user}
              openSignIn={openSignIn}
            />
          }
        />

        <Route path="/clips" element={<VeeClipsFeed />} />
        {/* NEW: Dedicated SEO route for single clips */}
        <Route path="/clip/:id" element={<VeeClipsFeed />} />

      {/* --- 👻 SECRET GHOST ROUTE: ADMIN CENTER --- */}
        <Route 
          path="/x-sanguinius-40k" 
          element={
            isAuthLoading ? (
              // 1. If Firebase is still "thinking," show the loader so we don't accidentally redirect you
              <PageLoader /> 
            ) : ["NQIVlFkrhhcV30QStniNyf1iRLU2", "fYLhnnD8D4NqpXAjPZ4ULNxVc1y2","g8Gh7pWPtqgxhZdEzo7KqZqQk3G2","4PLnYsAHLxSk6cpztc4Fh7n0E3D3"].includes(user?.uid) ? (
              // 2. Once Firebase is ready, check if your ID is in the list
              <AdminDashboard user={user} />
            ) : (
              // 3. If Firebase is ready and you ARE NOT an admin, then kick you to Home
              <Navigate to="/" replace />
            )
          } 
        />

        {/* --- LIGHTWEIGHT ROUTES (Instant) --- */}
        <Route path="/" element={renderWithShell(Home)} />

        <Route
          path="/ranking"
          element={
            <NoPartnerLayout menuActive={menuActive} setMenuActive={setMenuActive}>
              <Ranking />
            </NoPartnerLayout>
          }
        />

        <Route
          path="/about"
          element={
            <NoPartnerLayout menuActive={menuActive} setMenuActive={setMenuActive}>
              <About />
            </NoPartnerLayout>
          }
        />

        <Route
          path="/settings"
          element={
            <NoPartnerLayout menuActive={menuActive} setMenuActive={setMenuActive}>
              <Settings
                user={user}
                userCountry={userCountry}
                onLogout={onLogout}
                autoSearchNext={autoSearchNext}
                setAutoSearchNext={setAutoSearchNext}
                openSignIn={openSignIn}
              />
            </NoPartnerLayout>
          }
        />

        {/* SEO LANDING PAGES */}
        <Route path="/random-video-chat" element={renderWithShell(RandomVideoChat)} />
        <Route path="/meet-strangers-online" element={renderWithShell(MeetStrangersOnline)} />
        <Route path="/talk-to-strangers" element={renderWithShell(TalkToStrangers)} />
        <Route path="/video-chat-with-strangers" element={renderWithShell(VideoChatWithStrangers)} />
        <Route path="/free-online-video-chat" element={renderWithShell(FreeOnlineVideoChat)} />
        
        {/* COMPETITOR PAGES */}
        <Route path="/ome-tv-alternative" element={renderWithShell(OmeTVAlternative)} />
        <Route path="/monkey-app-alternative" element={renderWithShell(MonkeyAlternative)} />
        <Route path="/omegle-alternative" element={renderWithShell(OmegleAlternative)} />
        <Route path="/chatroulette-alternative" element={renderWithShell(ChatrouletteAlternative)} />
        <Route path="/coomeet-alternative" element={renderWithShell(CooMeetAlternative)} />
        <Route path="/emerald-chat-alternative" element={renderWithShell(EmeraldChatAlternative)} />
        <Route path="/azar-alternative" element={renderWithShell(AzarAlternative)} />

        {/* GAMES & FEATURES PAGES */}
        <Route path="/trivia-video-chat" element={renderWithShell(TriviaVideoChat)} />
        <Route path="/laugh-challenge-chat" element={renderWithShell(LaughChallenge)} />
        <Route path="/tic-tac-toe-strangers" element={renderWithShell(TicTacToeVideoChat)} />
        <Route path="/connect-4-video-chat" element={renderWithShell(Connect4VideoChat)} /> {/* <--- ADD THIS */}

        {/* PAIN POINT PAGES */}
        <Route path="/ad-free-video-chat" element={renderWithShell(AdFreeChat)} />
        <Route path="/stranger-chat-no-login" element={renderWithShell(NoLoginChat)} />

        {/* 404 CATCH-ALL */}
        <Route path="*" element={
          <NoPartnerLayout menuActive={menuActive} setMenuActive={setMenuActive}>
            <div style={{textAlign: 'center', padding: '50px'}}>
              <h1>404</h1>
              <p>Page not found</p>
            </div>
          </NoPartnerLayout>
        } />

      </Routes>

      {showInstallButton && (
        <button onClick={onInstallClick} className="install-button">
          📲 Install Talkvee App
        </button>
      )}
    </Suspense>
  );
}